/**
 * Kho bai tap - the school's shared question bank.
 *
 * Questions arrive from the AI Quiz Supporter export (source 'ai-quiz-supporter') or are
 * written by hand in the editor (source 'manual'). The bank is IZTeach's tenant-wide one
 * (izteach-examination-service /question), the same bank the admin's /question-bank edits, so
 * every teacher reads and may edit every question. izteach.ts maps between the two shapes.
 */

import { i18n } from '../i18n/index.ts';

export type QuestionBankType = 'single-choice' | 'multiple-choice' | 'boolean' | 'text';
export type QuestionBankSource = 'ai-quiz-supporter' | 'manual';
export type QuestionBankLevel = 1 | 2 | 3;

export interface QuestionBankOption {
  /** Sanitized HTML, same contract as the AI mapper's output. */
  content: string;
  isCorrect: boolean;
  /** The IZ answer key. Kept across edits so past submissions still point at the same answer. */
  key?: string;
}

/** Snapshot of the lecture a question is attached to - the name is kept so the row renders offline. */
export interface QuestionBankLessonRef {
  courseId: string;
  unitId: string;
  unitName: string;
}

export interface QuestionBankItem {
  id: string;
  /** Sanitized HTML. */
  content: string;
  type: QuestionBankType;
  level: QuestionBankLevel;
  options: QuestionBankOption[];
  explanation?: string;
  source: QuestionBankSource;
  /** Chat that produced the questions. Client-side only - the IZ bank stores no such field. */
  chatId?: string;
  lessonRef?: QuestionBankLessonRef;
  /** IZ user id stamped from the token on create - what the "Của tôi" facet asks the server for. */
  createdBy?: string;
  /** The IZ `config` as the server holds it. PUT replaces config whole, so keys the board does not own ride along. */
  izConfig?: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

/** Fields only the server sets or the store carries for it. */
type ServerOwned = 'id' | 'createdAt' | 'updatedAt' | 'source' | 'createdBy' | 'izConfig';

/** A question before it is saved: the server assigns id and timestamps, the action picks source. */
export type QuestionBankInput = Omit<QuestionBankItem, ServerOwned>;

/** Fields the editor may rewrite. Identity, provenance and creation time stay put. */
export type QuestionBankPatch = Partial<Omit<QuestionBankItem, ServerOwned>>;

export interface QuestionBankFilter {
  /** Substring match on content, case-insensitive. */
  search?: string;
  level?: QuestionBankLevel | 'all';
  type?: QuestionBankType | 'all';
  /** The tab: 'ai-quiz-supporter' is Kho đề AI, anything else Kho đề gốc (every row the AI did not make). */
  source?: QuestionBankSource | 'all';
  lessonRefUnitId?: string;
  /** True asks the server for this teacher's questions only (createdBy); unset is the whole school. */
  mine?: boolean;
}

export const QUESTION_BANK_LEVELS: QuestionBankLevel[] = [1, 2, 3];

export const QUESTION_BANK_TYPES: QuestionBankType[] = ['single-choice', 'multiple-choice', 'boolean', 'text'];

// Getters, not strings: each read translates at render time, so a language switch shows without a reload.
// (A plain t() here would run at import time and freeze the language.)
export const LEVEL_LABELS: Record<QuestionBankLevel, string> = {
  get 1() { return i18n.t('questionBank:level.easy'); },
  get 2() { return i18n.t('questionBank:level.medium'); },
  get 3() { return i18n.t('questionBank:level.hard'); },
};

export const TYPE_LABELS: Record<QuestionBankType, string> = {
  get 'single-choice'() { return i18n.t('questionBank:type.singleChoice'); },
  get 'multiple-choice'() { return i18n.t('questionBank:type.multipleChoice'); },
  get boolean() { return i18n.t('questionBank:type.boolean'); },
  get text() { return i18n.t('questionBank:type.text'); },
};

export const SOURCE_LABELS: Record<QuestionBankSource, string> = {
  get 'ai-quiz-supporter'() { return i18n.t('questionBank:source.aiQuizSupporter'); },
  get manual() { return i18n.t('questionBank:source.manual'); },
};

/** Narrow the mapper's free-form type strings to the bank's union, defaulting to trắc nghiệm. */
export function toQuestionBankType(value: string | undefined): QuestionBankType {
  return QUESTION_BANK_TYPES.filter((type) => type === value)[0] || 'single-choice';
}
